
/**
 * PositCard.tsx
 * 
 * Enhanced card component for displaying a Posit item with inline management actions.
 * Provides quick access to all posit management features without navigation.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ThumbsUp, 
  User, 
  MessageSquare, 
  Pencil, 
  Share2, 
  
  Globe, 
  Lock,
  MoreVertical,
  
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { format } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import ShareDialog from "./ShareDialog";
import EditPositSettingsDialog from "./EditPositSettingsDialog";
// Delete action is now only available from within Settings dialog
import PositPlusLogo from "@/components/shared/PositPlusLogo";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

// Enhanced Posit type with B2C fields
interface EnhancedPosit extends Tables<"posits"> {
  users?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    handle: string;
  };
  rating_percentage?: number;
  total_reviews?: number;
  has_subscription?: boolean;
  questions_remaining?: number;
  public_description?: string | null;
}

interface PositCardProps {
  posit: EnhancedPosit;
  isOwner?: boolean;
  isPremium?: boolean;
  showCreator?: boolean;
  showSubscriptionStatus?: boolean;
  variant?: 'default' | 'compact';
}

const PositCard: React.FC<PositCardProps> = ({ 
  posit, 
  isOwner = false, 
  isPremium = false,
  showCreator = true,
  showSubscriptionStatus = true,
  variant = 'default'
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Format the created_at date
  const formattedDate = posit.created_at
    ? format(new Date(posit.created_at), "MMM d, yyyy")
    : "Unknown date";

  // Determine the view link based on ownership and context
  const chatLink = `/chat/${posit.id}`;
  const editLink = `/update-posit/${posit.id}`;

  // Creator name
  const creatorName = posit.users 
    ? `${posit.users.first_name || ''} ${posit.users.last_name || ''}`.trim() || posit.users.handle
    : 'Unknown Creator';

  // Rating display
  const hasRating = (posit.total_reviews || 0) > 0;
  const ratingPercentage = posit.rating_percentage || 0;

  // Price display
  const formatPrice = (price: number | null) => {
    if (!price) return 'Free';
    return `$${price.toFixed(2)}/m`;
  };
  const pricePillText = posit.is_paid ? formatPrice(posit.subscription_price) : null;
  const isCompact = variant === 'compact';
  const description = posit.public_description || posit.posit_description || '';

  // Handle privacy cycle (direct click to change)
  const handlePrivacyCycle = async () => {
    if (!isOwner) return;
    
    let newPrivacy: 'public' | 'private' | 'exclusive';
    
    // Cycle through privacy options
    switch (posit.privacy) {
      case 'public':
        newPrivacy = isPremium ? 'exclusive' : 'public'; // Skip to exclusive if premium, stay public if not
        break;
      case 'exclusive':
        newPrivacy = isPremium ? 'private' : 'public';
        break;
      case 'private':
        newPrivacy = 'public';
        break;
      default:
        newPrivacy = 'public';
    }

    // Check premium requirement
    if (!isPremium && (newPrivacy === 'private' || newPrivacy === 'exclusive')) {
      toast({
        title: "Posit Plus required",
        description: "Upgrade to use private and exclusive privacy settings.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Use secure edge function for atomic privacy changes
      const { updatePositPrivacySecurely } = await import('../../utils/secureApiCalls');
      
      const result = await updatePositPrivacySecurely(posit.id, newPrivacy);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      toast({
        title: "Privacy updated",
        description: `Posit is now ${newPrivacy}.`
      });

      // Refresh the posits list
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-posits", user.id] });
      }
    } catch (error) {
      toast({
        title: "Couldn’t update privacy",
        description: (error as any).message || "Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle delete completion
  const handleDeleted = () => {
    // Additional cleanup if needed
  };

  // Handle settings updated
  const handleSettingsUpdated = () => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ["user-posits", user.id] });
    }
  };

  // Self-test controls removed from inline actions; available in admin tools only

  // Privacy icon helper
  const getPrivacyIcon = (privacy: string) => {
    switch (privacy) {
      case 'public':
        return <Globe className="w-3 h-3" />;
      case 'private':
        return <Lock className="w-3 h-3" />;
      case 'exclusive':
        return <Share2 className="w-3 h-3" />;
      default:
        return <Globe className="w-3 h-3" />;
    }
  };

  // Privacy color helper  
  const getPrivacyColor = (privacy: string) => {
    switch (privacy) {
      case 'private':
        return 'secondary';
      case 'exclusive':
        return 'default';
      default:
        return 'outline';
    }
  };

  return (
    <div className="border border-border rounded-lg p-4 hover:shadow-md transition-all duration-200 bg-card">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Left Section - Title and Description */}
        <div className="flex-1 min-w-0 w-full sm:w-auto">
          {/* Title and Date Row */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h3 className={`${isCompact ? 'text-base' : 'text-sm'} font-semibold leading-tight min-w-0 line-clamp-2 overflow-hidden text-ellipsis whitespace-normal ${isCompact ? 'break-words' : 'break-keep'}` }>
                {posit.title}
              </h3>
              {isCompact && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {pricePillText && (
                    <span className="flex-shrink-0 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">
                      {pricePillText}
                    </span>
                  )}
                  <span className="flex items-center gap-1 flex-shrink-0 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">
                    {getPrivacyIcon(posit.privacy)}
                    {posit.privacy}
                  </span>
                </div>
              )}
            </div>
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {formattedDate}
            </span>
          </div>
          
          {/* Badges Row */}
          {!isCompact && (
            <div className="flex items-center gap-2 mb-2">
              {/* Monetization Badge */}
              {posit.is_monetized && (
                <Badge 
                  variant={posit.is_paid ? "default" : "secondary"} 
                  className="text-xs px-1.5 py-0.5"
                >
                  {posit.is_paid ? (
                    <span className="text-xs">{formatPrice(posit.subscription_price)}</span>
                  ) : (
                    'Free'
                  )}
                </Badge>
              )}
              
              {/* Privacy Badge - Direct Click */}
              <Badge 
                variant={getPrivacyColor(posit.privacy)} 
                className={`text-xs px-1.5 py-0.5 flex items-center gap-1 ${isOwner ? 'cursor-pointer hover:opacity-80' : ''}`}
                onClick={isOwner ? handlePrivacyCycle : undefined}
              >
                {getPrivacyIcon(posit.privacy)}
                {posit.privacy}
              </Badge>
              
              {/* Creator Info */}
              {showCreator && posit.users && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <User className="w-3 h-3" />
                  <span>{creatorName}</span>
                </div>
              )}
              
              {/* Rating Display */}
              {hasRating && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ThumbsUp className="w-3 h-3 text-green-600" />
                  <span>{ratingPercentage}% ({posit.total_reviews})</span>
                </div>
              )}
              
              {/* Subscription Status */}
              {showSubscriptionStatus && posit.is_paid && (
                <>
                  {posit.has_subscription ? (
                    <Badge variant="outline" className="text-xs text-green-600 px-1.5 py-0.5">
                      Subscribed
                    </Badge>
                  ) : (
                    posit.questions_remaining !== undefined && posit.questions_remaining >= 0 && (
                      <span className="text-xs text-muted-foreground">
                        {posit.questions_remaining} free questions left
                      </span>
                    )
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Description */}
          {description && (
            isCompact ? (
              <div className="mt-1">
                <p className="text-sm text-muted-foreground leading-snug line-clamp-3">{description}</p>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground prose prose-xs max-w-none">
                <ReactMarkdown>
                  {description}
                </ReactMarkdown>
              </div>
            )
          )}
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto posit-card-actions">
          {isOwner ? (
            <>
              {/* Desktop/tablet: show all actions inline */}
              <div className="hidden sm:flex items-center gap-2">
                <Button asChild size="sm" className="w-9 p-0">
                  <Link to={chatLink}>
                    <MessageSquare className="w-3 h-3" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-9 p-0">
                  <Link to={editLink}>
                    <Pencil className="w-3 h-3" />
                  </Link>
                </Button>

                <ShareDialog 
                  positId={posit.id} 
                  isPremium={isPremium} 
                  buttonClassName="px-3"
                  buttonSize="sm"
                  showLabel={false}
                />

                <EditPositSettingsDialog
                  positId={posit.id}
                  currentTitle={posit.title}
                  currentPublicDescription={posit.public_description}
                  currentPrivacy={posit.privacy}
                  currentIsMonetized={posit.is_monetized || false}
                  currentPrice={posit.subscription_price}
                  currentResponseType={posit.response_type || "you"}
                  onSettingsUpdated={handleSettingsUpdated}
                  buttonClassName="px-3 flex items-center justify-center"
                  buttonSize="sm"
                  showLabel={false}
                />

                {/* Delete action removed from toolbar; it now lives inside Settings dialog */}
              </div>

              {/* Mobile: keep primary (Chat/Edit) inline, move others to a bottom sheet */}
              <div className="flex sm:hidden items-center gap-2 w-full">
                <div className="flex items-center gap-2">
                  <Button asChild size="sm" className="w-9 p-0">
                    <Link to={chatLink}>
                      <MessageSquare className="w-3 h-3" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="w-9 p-0">
                    <Link to={editLink}>
                      <Pencil className="w-3 h-3" />
                    </Link>
                  </Button>
                </div>
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="ml-auto w-9 p-0">
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="space-y-4">
                    <SheetHeader>
                      <SheetTitle>Actions</SheetTitle>
                    </SheetHeader>
                    <div className="grid grid-cols-2 gap-2">
                      <ShareDialog 
                        positId={posit.id} 
                        isPremium={isPremium} 
                        buttonClassName="w-full justify-center"
                        buttonSize="sm"
                        showLabel={true}
                        iconSize="sm"
                      />
                      <EditPositSettingsDialog
                        positId={posit.id}
                        currentTitle={posit.title}
                        currentPublicDescription={posit.public_description}
                        currentPrivacy={posit.privacy}
                        currentIsMonetized={posit.is_monetized || false}
                        currentPrice={posit.subscription_price}
                        currentResponseType={posit.response_type || "you"}
                        onSettingsUpdated={handleSettingsUpdated}
                        buttonClassName="w-full justify-center"
                        buttonSize="sm"
                        showLabel={true}
                      />
                      {/* Self-test and delete are not shown in the mobile overflow; delete is in Settings */}
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </>
          ) : (
            <Button asChild size="sm">
              <Link to={chatLink}>
                {posit.is_paid && !posit.has_subscription 
                  ? `Try ${formatPrice(posit.subscription_price)}`
                  : showCreator 
                    ? "View Details"
                    : "Chat with Posit"
                }
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositCard;
